import { CircleIcon, CircleSmallIcon } from 'lucide-react-native';
import React, { type ReactElement } from 'react';
import { type GestureResponderEvent } from 'react-native';
import { isWeb, Label, Separator, View, XStack, YStack } from 'tamagui';

import { hitSlop } from '../componentHelpers';
import type { ContainerComponent } from '../containers/Containers';
import { Icon, type IconType } from '../icons/Icon';
import { Shadows } from '../Shadows';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
} from '../text/Typography';

const getRadioColor = (
    disabled: boolean,
    isSelected?: boolean,
    isPositive?: boolean,
) => {
    if (disabled) return '$accent600';
    if (isSelected && isPositive) return '$green900';
    if (isSelected) return '$accent900';
    return '$gray1200';
};

interface RadioInputProps {
    tone?: 'neutral' | 'positive';
    isSelected?: boolean;
    disabled?: boolean;
    onPress?: (e?: GestureResponderEvent) => void;
}

export const RadioInput = ({
    tone,
    isSelected,
    disabled = false,
    onPress,
}: RadioInputProps) => {
    const isPositive = tone === 'positive';
    const radioColor = getRadioColor(disabled, isSelected, isPositive);
    const radioSize = 22;
    const selectedCircleSize = radioSize - 4;
    const selectedCirclePosition = (radioSize - selectedCircleSize) / 2;

    return (
        <View
            onPress={onPress}
            disabled={disabled}
            minWidth={radioSize}
            width={radioSize}
            margin={1}
            backgroundColor={disabled ? '$accent300' : undefined}
            animation="quicker"
            cursor={disabled ? 'default' : 'pointer'}
            pointerEvents={disabled ? 'none' : 'auto'}
            hoverStyle={isWeb && !disabled && { ...Shadows.RING }}
            focusStyle={isWeb && !disabled && { ...Shadows.RING }}
            pressStyle={{
                scale: 0.97,
                ...Shadows.RING_ACTIVE,
            }}
            borderRadius={20}
            flex={0}
            {...hitSlop(4)}
        >
            <Icon
                icon={CircleIcon}
                color={radioColor}
                size={radioSize}
                strokeWidth={isSelected ? 2 : 1.5}
            />
            {isSelected && (
                <Icon
                    icon={CircleSmallIcon}
                    color={radioColor}
                    fill={radioColor}
                    size={selectedCircleSize}
                    position="absolute"
                    left={selectedCirclePosition}
                    top={selectedCirclePosition}
                />
            )}
        </View>
    );
};

interface RadioInputWithLabelProps extends RadioInputProps {
    label: string;
}

export const RadioInputWithLabel = ({
    label,
    tone,
    isSelected,
    disabled = false,
    onPress,
}: RadioInputWithLabelProps) => {
    return (
        <XStack
            alignItems="flex-start"
            justifyContent="flex-start"
            paddingVertical={2}
        >
            <RadioInput
                isSelected={isSelected}
                tone={tone}
                disabled={disabled}
                onPress={onPress}
            />
            <Label
                onPress={onPress}
                disabled={disabled}
                fontWeight="$1"
                fontSize={16}
                cursor={disabled ? 'default' : 'pointer'}
                color={disabled ? '$gray800' : '$black'}
                pointerEvents={disabled ? 'none' : undefined}
                lineHeight={22}
                marginTop={1}
                paddingLeft={8}
            >
                {label}
            </Label>
        </XStack>
    );
};

const getRowBorderColor = (
    disabled: boolean,
    isSelected?: boolean,
    isPositive?: boolean,
) => {
    if (isSelected && isPositive) return disabled ? '$gray200' : '$green500';
    return '$white';
};

const getRowBackgroundColor = (
    disabled: boolean,
    isSelected?: boolean,
    isPositive?: boolean,
) => {
    if (isSelected && isPositive) return disabled ? '$gray100' : '$green300';
    return '$white';
};

const getRowHoverBackgroundColor = (
    disabled: boolean,
    isSelected?: boolean,
    isPositive?: boolean,
) => {
    if (disabled) return '$white';
    if (isSelected && isPositive) return '$green300';
    return '$gray100';
};

interface RadioInputRowProps extends Omit<
    RadioInputProps,
    'isSelected' | 'onPress'
> {
    isSelected: boolean;
    onPress: () => void;
    label: string;
    labelFontWeight?: 'regular' | 'emphasized';
    labelIcon?: IconType;
    details?: string | ReactElement;
    detailsHiddenUntilSelected?: boolean;
    leftAlignOnWideScreen?: boolean;
}

export const RadioInputRow = ({
    label,
    labelFontWeight = 'emphasized',
    labelIcon,
    isSelected,
    tone = 'neutral',
    disabled = false,
    details,
    detailsHiddenUntilSelected = false,
    onPress,
    leftAlignOnWideScreen,
}: RadioInputRowProps) => {
    const isPositive = tone === 'positive';
    const textColor = disabled ? '$gray800' : '$black';
    const subLabelTextColor = disabled ? '$gray700' : '$gray900';
    const borderColor = getRowBorderColor(disabled, isSelected, isPositive);
    const backgroundColor = getRowBackgroundColor(
        disabled,
        isSelected,
        isPositive,
    );
    const hoverBackgroundColor = getRowHoverBackgroundColor(
        disabled,
        isSelected,
        isPositive,
    );

    const RadioComponent = () => (
        <RadioInput
            isSelected={isSelected}
            tone={tone}
            disabled={disabled}
            onPress={(e) => {
                e?.stopPropagation();
                onPress?.();
            }}
        />
    );

    const TextComponent =
        labelFontWeight === 'emphasized'
            ? BodyMediumEmphasized
            : BodyMediumRegular;

    const DetailsComponent: ContainerComponent = ({ children }) =>
        typeof children === 'string' ? (
            <BodySmallRegular color={subLabelTextColor}>
                {children}
            </BodySmallRegular>
        ) : (
            <XStack
                width="100%"
                onPress={(e) => {
                    e.stopPropagation();
                }}
            >
                {children}
            </XStack>
        );

    const showDetails =
        details != null && (detailsHiddenUntilSelected ? isSelected : true);

    return (
        <XStack
            width="100%"
            justifyContent="space-between"
            alignItems="flex-start"
            gap={16}
            paddingHorizontal={isWeb ? 16 : 8}
            paddingVertical={16}
            borderWidth={1}
            borderRadius={12}
            borderColor={borderColor}
            backgroundColor={backgroundColor}
            onPress={onPress}
            cursor={disabled ? 'default' : 'pointer'}
            pointerEvents={disabled ? 'none' : undefined}
            animation="quick"
            hoverStyle={{
                backgroundColor: hoverBackgroundColor,
            }}
            opacity={1}
            pressStyle={
                !disabled && {
                    opacity: 0.6,
                }
            }
        >
            {leftAlignOnWideScreen && (
                <View
                    display="none"
                    paddingRight={16}
                    $sm={{ display: 'flex' }}
                >
                    <RadioComponent />
                </View>
            )}
            {labelIcon && (
                <Icon
                    icon={labelIcon}
                    color={textColor}
                    size={16}
                    marginTop={4 /* minor adjustment for vertical alignment */}
                />
            )}
            <YStack
                flex={1}
                marginTop={1 /* minor adjustment for vertical alignment */}
            >
                <TextComponent color={textColor}>{label}</TextComponent>
                {showDetails && <DetailsComponent>{details}</DetailsComponent>}
            </YStack>
            <View $sm={leftAlignOnWideScreen && { display: 'none' }}>
                <RadioComponent />
            </View>
        </XStack>
    );
};

interface RadioInputRowListProps {
    items: RadioInputRowProps[];
    hasSeparators?: boolean;
}

export const RadioInputRowList = ({
    items,
    hasSeparators = true,
}: RadioInputRowListProps) => {
    return (
        <YStack width="100%">
            {items.map((props, index) => (
                <React.Fragment key={index}>
                    <RadioInputRow {...props} />
                    {hasSeparators && index !== items.length - 1 && (
                        <Separator marginHorizontal={16} marginVertical={2} />
                    )}
                </React.Fragment>
            ))}
        </YStack>
    );
};
